import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { AdminTools } from 'Feature/Admin/AdminTools';
import { AgencyCodesPage } from 'Feature/AgencyCodes/AgencyCodesPage';
import { AgencyCodesErrorFallback } from 'Feature/AgencyCodes/AgencyCodesErrorFallback';
import AgencyCacheTool from 'Feature/AgencyTools/AgencyCacheTool';
import ManageBLPortalUsers from 'Feature/AgencyTools/ManageBLPortalUsers';
import MarketingResourceCenter from 'Feature/AgencyTools/MarketingResourceCenter';
import NewsArticleMetadata from 'Feature/PageContent/News/NewsArticleMetadata';
import NewsArticleTitle from 'Feature/PageContent/News/NewsArticleTitle';
import ClaimQuestions from 'Feature/Claims/ClaimQuestions';
import FindAnAdjuster from 'Feature/Claims/FindAnAdjuster';
import WaterMitigation from 'Feature/Claims/WaterMitigation';
import { CoveoSearchPage } from 'Feature/Coveo/CoveoSearchPage';
import AgencySweepReport from 'Feature/Customers/AgencySweepReport';
import RecentActivities from 'Feature/Customers/RecentActivities/RecentActivities';
import BLHelpRequestForm from 'Feature/Forms/BLHelpRequestForm';
import CommissionAccessForm from 'Feature/Forms/CommissionAccessForm';
import Guides from 'Feature/NowNavigation/Guides';
import ImageRichTextTile from 'Feature/PageContent/Shared/Tiles/ImageRichTextTile';
import MyInsightsTile from 'Feature/PageContent/MyInsights/MyInsightsTile';
import RecentActivitiesTile from 'Feature/PageContent/RecentActivities/RecentActivitiesTile';
import ContentModal from 'Feature/PageContent/Shared/ContentModal';
import MoratoriumECatAlert from 'Feature/PageContent/Shared/MoratoriumECatAlert';
import PersonalizationRow from 'Feature/PageContent/Shared/PersonalizationRow';
import QuoteModal from 'Feature/Quoting/QuoteModal';
import HorizontalCreditPerk from 'Feature/Rewards/HorizontalCreditPerk';
import HorizontalPerk from 'Feature/Rewards/HorizontalPerk';
import UpdateObjectCTA from 'Feature/Rewards/UpdateObjectCTA';
import SalesCompUI from 'Feature/SalesComp/SalesCompUI';
import { FavoritesPanel } from 'Feature/Slider/Cards/FavoritesPanel';
import { KeyContactsPanel } from 'Feature/Slider/Cards/KeyContactsPanel';
import FilteredNewsTile from 'Feature/PageContent/News/FilteredNewsTile';
import { FlexibleTwoTabTile } from './Feature/PageContent/FlexibleTwoTabTile/FlexibleTwoTabTile';
import QualtricsRefreshForm from 'Utilities/Analytics/QualtricsRefreshForm';
import InstantIncentivesTile from 'Feature/PageContent/InstantIncentivesTile/InstantIncentivesTile';
import NewsArticleFeaturedImage from 'Feature/PageContent/News/NewsArticleFeaturedImage';
import MyFavoritesTile from 'Feature/Favorites/MyFavoritesTile';
import MyInsightsOneThirdTile from 'Feature/PageContent/MyInsights/MyInsightsOneThirdTile';
import NewsRecommendationTile from 'Feature/PageContent/News/NewsRecommendationTile';
import Confetti from 'Feature/Confetti/Confetti';
import SliderGame from 'Feature/PageContent/Game/SliderGame';
import MemoryGame from 'Feature/PageContent/Game/MemoryGame';
import Ceros from 'Feature/PageContent/Shared/Ceros';
import AskKodiak from 'Feature/PageContent/AskKodiak/AskKodiak';
import ManageMyFavorites from 'Feature/Favorites/ManageMyFavorites';
import QuotedNotSold from 'Feature/Quoting/QuotedNotSold';
import StandardRichText from 'Foundation/Components/StandardRichText';
import StandardCallOut from 'Foundation/Components/StandardCallOut';
import { useUserData } from 'Foundation/User/UserContext';
import { useAgentQuoteData } from 'Foundation/User/QuoteDataContext';
import { useAgencyMetrics } from 'Foundation/Agency/AgencyMetricsContext';
import { useUserCampaigns } from 'Foundation/User/UserCampaignsContext';
import { replaceUserTokens } from 'Utilities/TokenHelper';
import L2NavigationStandard from 'Feature/NowNavigation/L2NavigationStandard';
import LMDSButton from 'Foundation/Components/LMDSButton';
import LinkBox from 'Feature/LinkBox/LinkBox';

type IComponentMetaData = {
  comp: any;
  tag: string;
};

export const Subapp = () => {
  const { userData } = useUserData();
  const { agentQuoteData } = useAgentQuoteData();
  const { agencyMetrics } = useAgencyMetrics();
  const { userCampaigns, getUserCampaigns } = useUserCampaigns();
  const content = useRef(document.getElementById('content') as HTMLDivElement);
  const originalContentTemplate = useRef<string | null>(null);
  const lastRenderedContentRef = useRef<string | null>(null);
  const [items, setItems] = useState<IComponentMetaData[]>([]);
  const [pendingContentRefresh, setPendingContentRefresh] = useState(false);

  // ALL HTML ELEMENT ATTRIBUTE NAMES BECOME LOWER CASE WHEN CONVERTED FROM HTML TO REACT COMPONENT
  // MAKE SURE YOUR REACT COMPONENTS USE LOWER CASE FOR PROPERTY ATTRIBUTE NAMES <MyReactComponent camelCaseAttri="Hello" /> -> props.camelcaseattri
  // Use data-source attribute if you want to spread a json object as properties for a component rather than have to do them individually in the view
  const componentTypes: IComponentMetaData[] = [
    { comp: AdminTools, tag: 'AdminTools' },
    { comp: AgencyCacheTool, tag: 'AgencyCacheTool' },
    { comp: AgencyCodesPage, tag: 'AgencyCodes' },
    { comp: AgencySweepReport, tag: 'AgencySweepReport' },
    { comp: AskKodiak, tag: 'AskKodiak' },
    { comp: BLHelpRequestForm, tag: 'BLHelpRequestForm' },
    { comp: LMDSButton, tag: 'LMDSButton' },
    { comp: Ceros, tag: 'Ceros' },
    { comp: ClaimQuestions, tag: 'ClaimQuestions' },
    { comp: CommissionAccessForm, tag: 'CommissionAccessForm' },
    { comp: Confetti, tag: 'Confetti' },
    { comp: StandardRichText, tag: 'StandardRichText' },
    { comp: StandardCallOut, tag: 'StandardCallOut' },
    { comp: ContentModal, tag: 'ContentModal' },
    { comp: CoveoSearchPage, tag: 'CoveoSearchPage' },
    { comp: FavoritesPanel, tag: 'favoritespanel' },
    { comp: FindAnAdjuster, tag: 'FindAnAdjuster' },
    { comp: FlexibleTwoTabTile, tag: 'FlexibleTwoTabTile' },
    { comp: Guides, tag: 'Guides' },
    { comp: HorizontalCreditPerk, tag: 'HorizontalCreditPerk' },
    { comp: HorizontalPerk, tag: 'HorizontalPerk' },
    { comp: ImageRichTextTile, tag: 'ImageRichTextTile' },
    { comp: InstantIncentivesTile, tag: 'InstantIncentivesTile' },
    { comp: KeyContactsPanel, tag: 'KeyContactsPanel' },
    { comp: ManageBLPortalUsers, tag: 'ManageBLPortalUsers' },
    { comp: ManageMyFavorites, tag: 'ManageMyFavorites' },
    { comp: MarketingResourceCenter, tag: 'MarketingResourceCenter' },
    { comp: MemoryGame, tag: 'MemoryGame' },
    { comp: MoratoriumECatAlert, tag: 'MoratoriumECatAlert' },
    { comp: MyFavoritesTile, tag: 'MyFavoritesTile' },
    { comp: MyInsightsOneThirdTile, tag: 'MyInsightsOneThirdTile' },
    { comp: MyInsightsTile, tag: 'MyInsightsTile' },
    { comp: NewsArticleFeaturedImage, tag: 'NewsArticleFeaturedImage' },
    { comp: NewsArticleMetadata, tag: 'NewsArticleMetadata' },
    { comp: NewsArticleTitle, tag: 'NewsArticleTitle' },
    { comp: NewsRecommendationTile, tag: 'NewsRecommendationTile' },
    { comp: FilteredNewsTile, tag: 'FilteredNewsTile' },
    { comp: L2NavigationStandard, tag: 'L2NavigationStandard' },
    { comp: PersonalizationRow, tag: 'PersonalizationRow' },
    { comp: QualtricsRefreshForm, tag: 'QualtricsRefreshForm' },
    { comp: QuotedNotSold, tag: 'QuotedNotSold' },
    { comp: QuoteModal, tag: 'QuoteModal' },
    { comp: RecentActivities, tag: 'RecentActivities' },
    { comp: RecentActivitiesTile, tag: 'RecentActivitiesTile' },
    { comp: SalesCompUI, tag: 'SalesCompUI' },
    { comp: SliderGame, tag: 'SliderGame' },
    { comp: UpdateObjectCTA, tag: 'UpdateObjectCTA' },
    { comp: WaterMitigation, tag: 'WaterMitigation' },
    { comp: LinkBox, tag: 'LinkBox' },
  ];

  // Computes the token-resolved markup from the pristine template using the current
  // context data. Pure (no DOM writes) so callers can also use it to detect whether a
  // context change would actually alter the rendered content before rebuilding.
  const computeTokenContent = (): string => {
    const template = originalContentTemplate.current ?? content.current?.innerHTML ?? '';
    try {
      // agentQuoteData gates agencyMetrics here to preserve existing behavior.
      return agentQuoteData
        ? replaceUserTokens(template, userData, agentQuoteData, agencyMetrics, userCampaigns)
        : replaceUserTokens(template, userData, undefined, undefined, userCampaigns);
    } catch (err) {
      console.error(err);
      return content.current?.innerHTML ?? '';
    }
  };

  const replaceTokensInDom = () => {
    if (!content.current) return;
    const newContent = computeTokenContent();
    if (newContent != content.current.innerHTML) {
      content.current.innerHTML = newContent;
    }
    lastRenderedContentRef.current = newContent;
  };

  const setupPortals = () => {
    for (const componentType of componentTypes) {
      var components = document.getElementsByTagName(componentType.tag);
      if (components) {
        for (const component of components) {
          const componentAttributes = getAttributes(component);
          const el = React.createElement(componentType.comp as any, componentAttributes, component.innerHTML);
          const nextportal = ReactDOM.createPortal(el, component);
          setItems((items) => [...items, { comp: nextportal, tag: componentType.tag }]);
        }
      }
    }
  };

  const handleAccordionDeepLink = (): Element | null => {
    const accordionId = new URLSearchParams(window.location.search).get('accordion');
    if (!accordionId) return null;

    const accordion: HTMLDivElement | null = document.querySelector(
      `#${accordionId.toLocaleLowerCase()} .accordion-placeholder-content`,
    );
    if (!accordion) return null;

    accordion.style.display = 'block';
    (accordion.previousElementSibling as HTMLInputElement)?.setAttribute('checked', '');
    return accordion.parentElement;
  };

  // Rewrites the .NET-rendered markup inside #content with token-resolved markup and
  // (re)creates the React portals for our custom element tags.
  const renderContent = () => {
    replaceTokensInDom();
    setupPortals();
  };

  useEffect(() => {
    if (content.current) {
      originalContentTemplate.current = content.current.innerHTML;
    }

    renderContent();

    const accordionTarget = handleAccordionDeepLink();
    requestAnimationFrame(() => {
      if (accordionTarget) { accordionTarget.scrollIntoView(); }
    });

    getUserCampaigns(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Any of the context providers (user, agent-quote, agency, campaigns) can resolve
  // asynchronously after mount, so re-run token replacement when ANY of them changes
  // — not just campaigns. Their values are stable references (backed by state), so
  // they are safe to depend on here.
  useEffect(() => {
    if (!originalContentTemplate.current) return;

    // Skip when the resolved markup wouldn't actually change (e.g. a context updated
    // that this page has no tokens for). This also covers the initial mount — whose
    // content was already rendered by the effect above — and avoids needlessly
    // remounting the portals.
    if (computeTokenContent() === lastRenderedContentRef.current) return;

    // The .NET view owns the initial DOM, so refreshing content necessarily destroys
    // the current portal host nodes and builds new ones. Tear the portals down in
    // this (normal) render FIRST; once React has committed the empty list and let go
    // of those host nodes, the effect below overwrites innerHTML and rebuilds. Doing
    // it in two phases avoids ripping host nodes out from under React mid-render,
    // which risks DOM "removeChild" errors.
    setItems([]);
    setPendingContentRefresh(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, agentQuoteData, agencyMetrics, userCampaigns]);

  useEffect(() => {
    if (!pendingContentRefresh) return;

    renderContent();

    // The accordion deep-link expansion lives in the markup we just rewrote, so
    // re-apply it (without re-scrolling) to keep an opened accordion open after a
    // late context refresh.
    handleAccordionDeepLink();

    setPendingContentRefresh(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingContentRefresh]);

  return (
    <>
      {items.map((item, i) => (
        <ErrorBoundary
          key={i}
          fallback={
            item.tag === 'AgencyCodes' ? <AgencyCodesErrorFallback /> : <div style={{ display: 'none' }}>{`ERROR in: ${item.tag}`}</div>
          }
        >
          {item.comp}
        </ErrorBoundary>
      ))}
    </>
  );
};

const getAttributes = (element: Element) => {
  var returnObj: any = {};
  if (element) {
    for (var att, i = 0, atts = element.attributes, n = atts.length; i < n; i++) {
      att = atts[i];
      if (att.nodeValue) {
        if (att.nodeName === 'data-source') {
          let parsedObj = tryParseJSONObject(att.nodeValue) ? JSON.parse(att.nodeValue) : null;
          if (parsedObj) {
            returnObj = {
              ...returnObj,
              ...parsedObj,
            };
          }
        } else {
          returnObj[att.nodeName] = tryParseJSONObject(att.nodeValue) ? JSON.parse(att.nodeValue) : att.nodeValue;
        }
      }
    }
  }
  return returnObj;
};

function tryParseJSONObject(jsonString: string) {
  try {
    var o = JSON.parse(jsonString);
    if (o && typeof o === 'object') {
      return o;
    }
  } catch (e) { }
  return false;
}
